import type { JsonSchema } from '@jsonforms/core';
import type { Layout } from '@jsonforms/core/src/models/uischema';
import type { ReadonlySignal } from '@preact/signals-react';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import type { Order } from '@thinkalpha/trading-ws-client/contracts/order.js';
import type { ErrorObject } from 'ajv';
import type { ReactiveInjectable } from 'src/features/ioc';
import type { SymbolPickerAction, InstrumentType } from 'src/features/symbol-picker/state';
import type { TableModel } from 'src/models/TableModel';
import type { WidgetHeaderElement } from 'src/models/WidgetModel';
import type { MultiLegMarketData } from 'src/routes/widgets/TradeProWidget/lib/MultiLegMarketData';
import type {
    OrderData,
    OrderAction,
    TradeProAction,
    MarketData,
    PriceTicker,
    Leg,
} from 'src/routes/widgets/TradeProWidget/state';
import type { TradingSocketConnectionStatus } from 'src/services/TradingSocketService';
import type { TradeProEvents, TradeProSignals } from 'src/types/EventBus';
import type { EventBus } from 'src/types/EventBus/EventBus';

export const DEFAULT_EXDESTINATION = 'basic';
export const DEFAULT_ROUTE = 'basic';

export interface TradeProWidgetModel extends ReactiveInjectable {
    init(tabId: string): void;
    eventBus: EventBus<TradeProSignals, TradeProEvents>;
    headerElements: WidgetHeaderElement[];
    readonly isReady: ReadonlySignal<boolean>;
    readonly tradingSocketConnectionStatus: ReadonlySignal<TradingSocketConnectionStatus>;
    readonly canSubmit: ReadonlySignal<boolean>;
    readonly canSubmitError: ReadonlySignal<string | null>;
    resetOrderData: () => void;
    marketDataTable: TableModel;
    marketData: ReadonlySignal<MarketData>;
    readonly underlyingSymbol: ReadonlySignal<string>;
    readonly instrument: ReadonlySignal<Instrument>;
    setInstrument: (instrument: Instrument) => void;
    readonly showTradeToast: ReadonlySignal<boolean>;
    setShowTradeToast: (value: boolean) => void;
    readonly skipTradeConfirmation: ReadonlySignal<boolean>;
    setSkipTradeConfirmation: (value: boolean) => void;
    readonly orderData: ReadonlySignal<OrderData>;
    setOrderData: (data: Partial<OrderData>, options?: { fromStoreSync?: boolean }) => void;
    readonly orderErrors: ReadonlySignal<ErrorObject[]>;
    setOrderErrors: (errors: ErrorObject[]) => void;
    setPriceByClickingATicker: (value: number | undefined, ticker: PriceTicker) => void;
    readonly executorsSchema: ReadonlySignal<Record<string, any>>;
    readonly executorsSchemaIsLoading: ReadonlySignal<boolean>;
    readonly executorsSchemaFetchingError: ReadonlySignal<Error | null>;
    readonly schema: ReadonlySignal<JsonSchema>;
    readonly uischema: ReadonlySignal<Layout>;
    readonly allowedInstrumentTypes: ReadonlySignal<InstrumentType[]>;
    readonly canRetryFormRender: ReadonlySignal<boolean>;
    readonly exDestinationsWithRoutes: ReadonlySignal<Record<string, string[]>>;
    dispatch: (action: TradeProAction) => void;
    reportFormRenderFailure: (error: Error) => void;
    sendOrder: (side: OrderAction) => Promise<Order | false>;
    isMultiLegWidget: boolean;
    readonly legs: ReadonlySignal<Leg[]>;
    readonly activeStrategy: ReadonlySignal<string | undefined>;
    readonly multiLegMarketData: MultiLegMarketData | null;
    addLeg: (type: InstrumentType) => void;
    updateLeg: (legId: string, updates: Partial<Leg>) => void;
    removeLeg: (legId: string) => void;
    setActiveStrategy: (strategy: string) => void;
    updateLegSymbolPicker: (legId: string, symbolPickerAction: SymbolPickerAction) => void;
    readonly allowedSides: ReadonlySignal<OrderAction[]>;
}
